import {
  ShopperExperienceDto,
  GameExperiencePayloadDto,
} from '@luckycart/xpn-types';

import {
  ApiService,
  apiServiceInstance,
  AxiosFetchSiteDataResponse,
} from './ApiService';
import { Game } from '../../common-types/GlobalExperienceState.type';

export class ExperienceService {
  private apiServiceInstance: ApiService;

  public constructor() {
    this.apiServiceInstance = apiServiceInstance;
  }

  public getSiteInformations(
    siteKey: string
  ): Promise<AxiosFetchSiteDataResponse> {
    return this.apiServiceInstance.fetchSiteData(siteKey);
  }

  public async getGamesInformations(
    shopperId: string,
    siteKey: string,
    cartId: string,
    targetExperienceId: string | null
  ): Promise<{ games: Game[]; firstGameData: Game | null }> {
    const games: ShopperExperienceDto[] = await this.apiServiceInstance
      .fetchGamesList(shopperId, siteKey)
      .then((response: ShopperExperienceDto[]) =>
        response.filter((game: ShopperExperienceDto) => {
          const payload = game.payload as unknown as GameExperiencePayloadDto;
          return payload?.cartId.toString() === cartId;
        })
      );

    const gamesData = await Promise.all(
      games.map(async (game: ShopperExperienceDto) => {
        const { operationId, legacyCampaignId, experienceId } = game;
        const campaignId = legacyCampaignId ?? operationId;

        const response = await apiServiceInstance
          .fetchGameExperience(shopperId, siteKey, campaignId, experienceId)
          .catch((error) => {
            throw error;
          });
          
        if (!response || response.toString().length === 0) {
          return null;
        }

        const { experience, operation, gameConfiguration } = response;
        return {
          operation,
          gameConfiguration: gameConfiguration.cfg.screens,
          thumbnailUrl: gameConfiguration.cfg.images.thumbnailUrl,
          experience,
        };
      })
    );

    const filteredGames = gamesData.filter((game) => game !== null);

    const gamesToDisplay = ExperienceService.getGamesToDisplay(
      filteredGames as Game[],
      targetExperienceId
    );

    return {
      firstGameData: gamesToDisplay.length > 0 ? gamesToDisplay[0] : null,
      games: gamesToDisplay,
    };
  }

  public static getGamesToDisplay(
    games: Game[],
    targetExperienceId: string | null
  ): Game[] {
    const nonAutoplayGames = ExperienceService.getNonAutoplayGames(games);
    const orderedGames = ExperienceService.getOrderedGames(
      nonAutoplayGames,
      targetExperienceId
    );

    const playableGames = ExperienceService.getPlayableGames(orderedGames);
    if (playableGames.length > 0) {
      return playableGames;
    }
    const canceledGames = ExperienceService.getCanceledGames(orderedGames);
    if (canceledGames.length > 0) {
      return canceledGames;
    }
    const alreadyPlayedGames =
      ExperienceService.getAlreadyPlayedGames(orderedGames);
    if (alreadyPlayedGames.length > 0) {
      return alreadyPlayedGames;
    }
    return [];
  }

  private static getOrderedGames(
    games: Game[],
    targetExperienceId: string | null
  ): Game[] {
    // First, sort games by operation priority (lower priority first)
    const sortedByPriority = games.sort((gameA, gameB) => {
      return gameA.operation.prio - gameB.operation.prio;
    });

    // Then, if there's a target experience, move it to the front
    if (targetExperienceId !== null) {
      const targetIndex = sortedByPriority.findIndex(
        (game) => game.experience.experienceId === targetExperienceId
      );
      if (targetIndex !== -1) {
        const [targetGame] = sortedByPriority.splice(targetIndex, 1);
        sortedByPriority.unshift(targetGame);
      }
    }

    return sortedByPriority;
  }

  private static getNonAutoplayGames(games: Game[]): Game[] {
    // eslint-disable-next-line no-extra-parens
    return games.filter(
      (game) =>
        (game.experience.payload as GameExperiencePayloadDto).isAutoplay !==
        true
    );
  }

  private static getPlayableGames(games: Game[]): Game[] {
    // eslint-disable-next-line no-extra-parens
    return games.filter(
      (game) =>
        (game.experience.payload as GameExperiencePayloadDto).isGamePlayable ===
        true
    );
  }

  private static getCanceledGames(games: Game[]): Game[] {
    // eslint-disable-next-line no-extra-parens
    return games.filter(
      (game) =>
        (game.experience.payload as GameExperiencePayloadDto).isAvailable ===
        false
    );
  }

  private static getAlreadyPlayedGames(games: Game[]): Game[] {
    // eslint-disable-next-line no-extra-parens
    return games.filter(
      (game) =>
        (game.experience.payload as GameExperiencePayloadDto).isConsumed ===
        true
    );
  }
}

export const experienceServiceInstance = new ExperienceService();
